<template>
  <v-app>
    <v-navigation-drawer app
                         clipped
                         color="secondary"
                         dark
                         temporary
                         v-bind:style="navigationDrawerStyle"
                         v-model="navigationDrawer"
                         v-if="!chromeless">
      <div v-on:click="closeNavigationDrawer">
        <router-view name="navigation" />
      </div>
    </v-navigation-drawer>

    <v-app-bar app
               class="app-bar"
               clipped-left
               color="secondary"
               dark
               flat
               v-if="!chromeless">
      <v-app-bar-nav-icon v-bind:aria-label="$t('menu')"
                          v-on:click="navigationDrawer = !navigationDrawer" />

      <router-link class="d-flex mr-4"
                   v-bind:to="{ name: 'home' }">
        <img alt="Foodtrucks United"
             class="app-logo"
             v-bind:src="logoUrl">
      </router-link>

      <v-toolbar-title class="d-none d-sm-block">
        <span class="font-weight-bold">Foodtrucks</span>
        United
      </v-toolbar-title>

      <div class="d-none d-md-flex ml-8">
        <v-btn active-class="accent--text"
               class="app-bar__link"
               text
               v-bind:exact-path="item.exact"
               v-bind:key="item.name"
               v-bind:to="{ name: item.name }"
               v-for="item in mainNavigationItems">
          <v-icon left>{{ item.icon }}</v-icon>
          {{ $t(item.label) }}
        </v-btn>
      </div>

      <v-spacer />

      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon
                 v-bind="attrs"
                 v-bind:aria-label="$t('contact')"
                 v-bind:href="`mailto:${contactEmail}`"
                 v-on="on">
            <v-icon>mdi-email-outline</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('contact') }}</span>
      </v-tooltip>

      <locale-block v-bind:current-locale-id="$i18n.locale"
                    v-bind:locales="locales"
                    v-on:set-locale="setLocale" />

      <account-block v-bind:display-name="displayName"
                     v-bind:email="email"
                     v-bind:hide-sign-in="true"
                     v-bind:is-authenticated="isAuthenticated"
                     v-bind:picture="picture"
                     v-on:sign-in="signIn({ redirectPath: $route.fullPath })"
                     v-on:sign-out="signOut">
        <template v-if="hasRole('FoodTruckFinderAdministrator') || hasRole('FoodTruckFinderEditor')">
          <v-list-item v-bind:to="{ name: 'administration-food-trucks' }">
            <v-list-item-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ $t('administration') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-divider />
        </template>
      </account-block>
    </v-app-bar>

    <v-main>
      <transition mode="out-in"
                  name="page">
        <router-view />
      </transition>
    </v-main>

    <v-bottom-navigation app
                         background-color="secondary"
                         color="accent"
                         dark
                         grow
                         v-if="showBottomNavigation">
      <v-btn v-bind:exact-path="item.exact"
             v-bind:key="item.name"
             v-bind:to="{ name: item.name }"
             v-for="item in mainNavigationItems">
        <span>{{ $t(item.label) }}</span>
        <v-icon>{{ item.icon }}</v-icon>
      </v-btn>
    </v-bottom-navigation>
  </v-app>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'

  import locales from '@/locales.json'
  import settings from '@/settings'

  import AccountBlock from '@/components/common/AccountBlock'
  import LocaleBlock from '@/components/common/LocaleBlock'

  export default {
    components: {
      AccountBlock,
      LocaleBlock
    },
    computed: {
      chromeless() {
        if (this.keepChromeless) {
          return true
        }

        if (!this.$route.query.chromeless) {
          return false
        }

        this.keepChromeless = true

        return this.$route.query.chromeless
      },
      contactEmail() {
        return settings.email
      },
      locales() {
        return locales
      },
      logoUrl() {
        return `${process.env.BASE_URL}images/logo.svg`
      },
      mainNavigationItems() {
        // The map is matched by path only so it stays active with query parameters (e.g. ?foodTruckId=1)
        return [
          { exact: true, icon: 'mdi-map-outline', label: 'map', name: 'home' },
          { icon: 'mdi-fire', label: 'spots', name: 'spots' },
          { icon: 'mdi-truck-outline', label: 'trucks', name: 'list' },
          { icon: 'mdi-handshake-outline', label: 'partners', name: 'partner-list' }
        ]
      },
      navigationDrawerStyle() {
        // Temporary drawers ignore "clipped", so keep it below the app bar ourselves
        const top = this.$vuetify.application.top

        return {
          maxHeight: `calc(100% - ${top}px)`,
          top: `${top}px`
        }
      },
      showBottomNavigation() {
        // Without the app bar (chromeless) this is the only way to get around
        return this.chromeless || this.$vuetify.breakpoint.smAndDown
      },
      ...mapGetters({
        displayName: 'auth/displayName',
        email: 'auth/email',
        hasRole: 'auth/hasRole',
        isAuthenticated: 'auth/isAuthenticated',
        picture: 'auth/picture'
      })
    },
    data: () => ({
      navigationDrawer: false,
      keepChromeless: false
    }),
    methods: {
      closeNavigationDrawer(event) {
        // Route changes close the drawer on their own, external links and the current route do not
        if (event.target.closest('.v-list-item')) {
          this.navigationDrawer = false
        }
      },
      setLocale({ localeId }) {
        this.$root.$i18n.locale = localeId
      },
      ...mapActions({
        signIn: 'auth/signIn',
        signOut: 'auth/signOut'
      })
    },
    name: 'App'
  }
</script>

<style scoped>
  /* Keeps the app bar (and its menu icon) above the overlay of the open navigation drawer */
  .app-bar {
    z-index: 8;
  }

  .app-bar__link {
    letter-spacing: normal;
    text-transform: none;
  }

  /* Shows only the truck; the tagline below it is cropped off as it is illegible at this size */
  .app-logo {
    height: 40px;
    object-fit: cover;
    object-position: top;
    width: 65px;
  }
</style>

<i18n>
  {
    "de": {
      "administration": "Administration",
      "contact": "Kontakt per E-Mail",
      "map": "Karte",
      "menu": "Menü",
      "partners": "Partner",
      "spots": "Spots",
      "trucks": "Trucks"
    },
    "en": {
      "administration": "Administration",
      "contact": "Contact via email",
      "map": "Map",
      "menu": "Menu",
      "partners": "Partners",
      "spots": "Spots",
      "trucks": "Trucks"
    }
  }
</i18n>